// src/pages/admin/AdminAbout.jsx
import React, { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import {
  getAboutContent, updateAboutContent,
  getCertificates, addCertificate, deleteCertificate,
} from "../../services/firestoreService";
import ImageUploader from "../../components/common/ImageUploader";
import Spinner from "../../components/common/Spinner";
import Modal from "../../components/common/Modal";
import toast from "react-hot-toast";
import { FiPlus, FiTrash2, FiSave, FiFileText, FiX } from "react-icons/fi";

// ── PDF helpers ──────────────────────────────────────────────────────────────
const isPDF = (url) => typeof url === "string" && url.toLowerCase().includes(".pdf");

const loadPdfJs = () =>
  new Promise((resolve, reject) => {
    if (window.pdfjsLib) { resolve(window.pdfjsLib); return; }
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
    s.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
      resolve(window.pdfjsLib);
    };
    s.onerror = reject;
    document.head.appendChild(s);
  });

const renderPDFThumb = async (url) => {
  try {
    const lib  = await loadPdfJs();
    const pdf  = await lib.getDocument(url).promise;
    const page = await pdf.getPage(1);
    const vp   = page.getViewport({ scale: 1.2 });
    const canvas = document.createElement("canvas");
    canvas.width  = vp.width;
    canvas.height = vp.height;
    await page.render({ canvasContext: canvas.getContext("2d"), viewport: vp }).promise;
    return canvas.toDataURL();
  } catch { return null; }
};

function usePDFThumb(url) {
  const [thumb, setThumb] = React.useState(null);
  React.useEffect(() => {
    if (!isPDF(url)) return;
    renderPDFThumb(url).then(setThumb);
  }, [url]);
  return thumb;
}

// ── AdminCertCard ─────────────────────────────────────────────────────────────
function AdminCertCard({ cert, onDelete }) {
  const pdf   = isPDF(cert.image);
  const thumb = usePDFThumb(cert.image);
  const [lightbox, setLightbox] = React.useState(false);

  const handleClick = () => {
    if (!cert.image) return;
    if (pdf) window.open(cert.image, "_blank");
    else setLightbox(true);
  };

  return (
    <>
      <div className="card p-4 flex flex-col items-center gap-3 relative group">
        {/* thumbnail */}
        <div
          className="cursor-pointer w-20 h-20 flex items-center justify-center rounded-xl overflow-hidden border border-gray-100 bg-gray-50 hover:border-primary-300 transition-colors"
          onClick={handleClick}
          title={pdf ? "Open PDF" : "View image"}
        >
          {cert.image ? (
            pdf ? (
              thumb ? (
                <img src={thumb} alt={cert.name} className="w-full h-full object-cover" />
              ) : (
                <div className="flex flex-col items-center justify-center gap-1 w-full h-full">
                  <FiFileText className="w-7 h-7 text-red-400" />
                  <span className="text-[9px] font-bold text-red-500 uppercase">PDF</span>
                </div>
              )
            ) : (
              <img src={cert.image} alt={cert.name} className="w-full h-full object-contain p-1" />
            )
          ) : (
            <span className="text-2xl">📜</span>
          )}
        </div>

        {/* PDF badge */}
        {pdf && cert.image && (
          <span className="absolute top-2 left-2 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded uppercase">PDF</span>
        )}

        <p className="text-sm font-semibold text-gray-900 text-center line-clamp-2">{cert.name}</p>
        {cert.issuedBy && <p className="text-xs text-gray-500 text-center line-clamp-1">{cert.issuedBy}</p>}

        <button
          onClick={() => onDelete(cert.id)}
          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 p-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-all"
        >
          <FiTrash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Image lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setLightbox(false)}
        >
          <div className="relative max-w-4xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <img
              src={cert.image}
              alt={cert.name}
              className="max-w-full max-h-[85vh] rounded-2xl object-contain shadow-2xl"
            />
            <button
              onClick={() => setLightbox(false)}
              className="absolute -top-3 -right-3 bg-white text-gray-800 rounded-full p-1.5 shadow-lg hover:bg-gray-100 transition-colors"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default function AdminAbout() {
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [certModal,    setCertModal]    = useState(false);
  const [certificates, setCertificates] = useState([]);
  const [certImage,    setCertImage]    = useState("");
  const [savingCert,   setSavingCert]   = useState(false);

  const { register, handleSubmit, control, reset } = useForm({
    defaultValues: {
      heroTitle: "", heroSub: "", mission: "", vision: "",
      whyUs: "", whoWeAreTitle: "", whoWeAreDesc: "",
      teamTitle: "", teamDesc: "",
      whyChooseUs: [{ icon: "🌿", title: "", desc: "" }],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: "whyChooseUs" });

  const { register: certRegister, handleSubmit: certSubmit, reset: certReset } = useForm();

  const load = async () => {
    const [about, certs] = await Promise.all([getAboutContent(), getCertificates()]);
    reset({
      heroTitle:      about.heroTitle      || "About AgroShop",
      heroSub:        about.heroSub        || "",
      mission:        about.mission        || "",
      vision:         about.vision         || "",
      whyUs:          about.whyUs          || "",
      whoWeAreTitle:  about.whoWeAreTitle  || "",
      whoWeAreDesc:   about.whoWeAreDesc   || "",
      teamTitle:      about.teamTitle      || "",
      teamDesc:       about.teamDesc       || "",
      whyChooseUs: about.whyChooseUs?.length ? about.whyChooseUs : [{ icon: "🌿", title: "", desc: "" }],
    });
    setCertificates(certs);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      await updateAboutContent(data);
      toast.success("About Us content saved!");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const onAddCert = async (data) => {
    setSavingCert(true);
    try {
      await addCertificate({ ...data, image: certImage, order: certificates.length + 1 });
      toast.success("Certificate added");
      setCertModal(false);
      certReset();
      setCertImage("");
      const certs = await getCertificates();
      setCertificates(certs);
    } catch { toast.error("Failed to add certificate"); }
    finally { setSavingCert(false); }
  };

  const handleDeleteCert = async (id) => {
    if (!window.confirm("Delete this certificate?")) return;
    await deleteCertificate(id);
    toast.success("Certificate deleted");
    setCertificates((prev) => prev.filter((c) => c.id !== id));
  };

  if (loading) return <Spinner center />;

  return (
    <div className="max-w-7xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">About Us Content</h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Hero */}
        <div className="card p-6">
          <h2 className="font-semibold text-gray-900 mb-4">Hero Section</h2>
          <div className="space-y-3">
            <div>
              <label className="label">Hero Title</label>
              <input {...register("heroTitle")} className="input" placeholder="About AgroShop" />
            </div>
            <div>
              <label className="label">Hero Subtitle</label>
              <textarea {...register("heroSub")} rows={2} className="input resize-none" placeholder="Short tagline..." />
            </div>
          </div>
        </div>

        {/* Mission, Vision, Why Us & Who We Are */}
        <div className="card p-6">
          <h2 className="font-semibold text-gray-900 mb-4">Mission, Vision & More</h2>
          <div className="space-y-3">
            <div>
              <label className="label">Mission</label>
              <textarea {...register("mission")} rows={3} className="input resize-none" placeholder="Your mission statement..." />
            </div>
            <div>
              <label className="label">Vision</label>
              <textarea {...register("vision")} rows={3} className="input resize-none" placeholder="Your vision statement..." />
            </div>
            <div>
              <label className="label">Why Us (short paragraph)</label>
              <textarea {...register("whyUs")} rows={3} className="input resize-none" placeholder="What distinguishes your company..." />
            </div>
            <div className="pt-2 border-t border-gray-100">
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3">Who We Are</p>
              <div className="space-y-3">
                <div>
                  <label className="label">Section Title</label>
                  <input {...register("whoWeAreTitle")} className="input" placeholder="Who We Are" />
                </div>
                <div>
                  <label className="label">Description</label>
                  <textarea {...register("whoWeAreDesc")} rows={3} className="input resize-none" placeholder="Describe your team or company..." />
                </div>
              </div>
            </div>
          </div>
        </div>

  
        {/* Team / Commitment */}
        <div className="card p-6">
          <h2 className="font-semibold text-gray-900 mb-4">Our Commitment / Team Section</h2>
          <div className="space-y-3">
            <div>
              <label className="label">Section Title</label>
              <input {...register("teamTitle")} className="input" placeholder="Our Commitment" />
            </div>
            <div>
              <label className="label">Description</label>
              <textarea {...register("teamDesc")} rows={4} className="input resize-none" placeholder="Write about your commitment or team..." />
            </div>
          </div>
        </div>

        <button type="submit" disabled={saving} className="btn-primary w-full flex items-center justify-center gap-2">
          {saving ? <><Spinner size="sm" /> Saving...</> : <><FiSave className="w-4 h-4" /> Save About Us Content</>}
        </button>
      </form>

      {/* Certificates */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Certificates</h2>
          <button onClick={() => setCertModal(true)} className="btn-primary flex items-center gap-2">
            <FiPlus className="w-4 h-4" /> Add Certificate
          </button>
        </div>
        {certificates.length === 0 ? (
          <div className="card p-8 text-center text-gray-500">
            <p className="text-3xl mb-2">📜</p>
            <p>No certificates yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {certificates.map((cert) => (
              <AdminCertCard key={cert.id} cert={cert} onDelete={handleDeleteCert} />
            ))}
          </div>
        )}
      </div>

      {/* Certificate Modal */}
      <Modal isOpen={certModal} onClose={() => setCertModal(false)} title="Add Certificate">
        <form onSubmit={certSubmit(onAddCert)} className="space-y-4">
          <div>
            <label className="label">Certificate Name *</label>
            <input {...certRegister("name", { required: true })} className="input" placeholder="e.g. FSSAI Certification" />
          </div>
          <div>
            <label className="label">Issued By</label>
            <input {...certRegister("issuedBy")} className="input" placeholder="e.g. Ministry of Food Processing" />
          </div>
          <div>
            <label className="label">Certificate Image</label>
            <ImageUploader folder="certificates" onUpload={(imgs) => setCertImage(imgs[0] || "")} existingImages={certImage ? [certImage] : []} maxFiles={1} />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={savingCert} className="btn-primary flex-1 flex items-center justify-center gap-2">
              {savingCert ? <><Spinner size="sm" /> Saving...</> : "Add Certificate"}
            </button>
            <button type="button" onClick={() => setCertModal(false)} className="btn-secondary">Cancel</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}